import {
  deriveAnnotationCamera,
  Vec3,
  type AvatarAnimationMapping,
  type CameraOptions,
  type JoystickMoveIntent,
  type ProjectedPoint,
  type RendererAdapter,
  type ScreenshotOptions,
  type Vec3Like,
  type Viewer,
  type ViewerAnimState,
  type ViewerAnnotation,
  type ViewerAvatarState,
  type ViewerEventMap,
  type ViewerInitOptions,
  type ViewerInputMode,
  type ViewerModelHandle,
  type ViewerNavigationMode,
  type ViewerPickResult,
  type ViewerSceneOptions,
  type WalkMoveIntent,
  type ViewerAnimTrack,
  type VrQualityPreset,
} from '@mindcloudxai/shared';

// ---------------------------------------------------------------------------
// AdapterViewer — RendererAdapter → Viewer 契约的兼容 shim。
//
// 为尚未提供原生 viewer 的渲染后端（three 等）提供 Viewer 接口：
// pick 映射到同步 raycast、focusAnnotation 走 flyToCamera、运镜不支持
// （anim 命令为 no-op，hasAnimation 恒为 false）。行为以"不回归"为准，
// 不在 shim 内补运镜等增强能力——后端应按契约实现原生 viewer。
// ---------------------------------------------------------------------------

type Listener = (...args: unknown[]) => void;

const IDLE_ANIM_STATE: ViewerAnimState = Object.freeze({
  hasAnimation: false,
  duration: 0,
  time: 0,
  paused: true,
  active: false,
}) as ViewerAnimState;

const DEFAULT_CAMERA: CameraOptions = {
  position: { x: 0, y: 1.5, z: 4 },
  target: { x: 0, y: 0, z: 0 },
};

export class AdapterViewer implements Viewer {
  /** 暴露底层 adapter，供业务侧接入 walk/collision 等后端特有扩展。 */
  readonly adapter: RendererAdapter;

  private container: HTMLElement | null = null;
  private initOptions: ViewerInitOptions = {};
  private annotations: ViewerAnnotation[] = [];
  private listeners = new Map<string, Set<Listener>>();
  private navModeCallback: ((mode: ViewerNavigationMode) => void) | null = null;

  readonly camera = {
    get: () => this.getCamera(),
    set: (camera: CameraOptions) => this.setCamera(camera),
    pick: (options: { clientX: number; clientY: number }) => this.pick(options),
    project: (point: Vec3Like) => this.projectPoint(point),
    captureScreenshot: (options?: ScreenshotOptions) => this.captureScreenshot(options),
  };

  readonly navigation = {
    getMode: () => this.getNavigationMode(),
    setMode: (mode: ViewerNavigationMode) => this.setNavigationMode(mode),
    reset: () => this.resetNavigation(),
    isWalkAvailable: () => this.isWalkAvailable(),
    walk: {
      setMoveIntent: (intent: WalkMoveIntent) => this.setWalkMoveIntent(intent),
      setJoystickIntent: (intent: JoystickMoveIntent) => this.setJoystickMoveIntent(intent),
      jump: () => this.jumpWalk(),
    },
    anim: {
      getState: () => this.getAnimState(),
      play: () => this.playAnim(),
      pause: () => this.pauseAnim(),
      togglePlayPause: () => this.togglePlayPause(),
      seek: (time: number) => this.seekAnim(time),
      setSpeed: (speed: number) => this.setAnimSpeed(speed),
      setTrack: (track: ViewerAnimTrack | null) => this.setAnimTrack(track),
    },
  };

  readonly scene = {
    getAnnotations: () => this.getAnnotations(),
    focusAnnotation: (index: number) => this.focusAnnotation(index),
  };

  readonly measure = {
    isActive: () => this.isMeasureActive(),
    setActive: (active: boolean) => this.setMeasureActive(active),
    toggle: () => this.toggleMeasure(),
    clear: () => this.clearMeasure(),
    setUnitSystem: (system: 'metric' | 'imperial') => this.setMeasureUnitSystem(system),
  };

  readonly avatar = {
    getState: () => this.getAvatarState(),
    setEnabled: (enabled: boolean) => this.setAvatarEnabled(enabled),
    setUrl: (url: string) => this.setAvatarUrl(url),
    setFollowCamera: (enabled: boolean) => this.setAvatarFollowCamera(enabled),
    setMapping: (mapping: Partial<AvatarAnimationMapping>) => this.setAvatarMapping(mapping),
  };

  readonly input = {
    setMode: (mode: ViewerInputMode) => this.setInputMode(mode),
    setGamingControls: (enabled: boolean) => this.setGamingControls(enabled),
  };

  readonly xr = {
    enterVr: (preset?: VrQualityPreset) => this.enterVr(preset),
  };

  // ThreeAdapter 尚未实现模型导入，提供 stub 满足 Viewer 接口契约
  readonly model = {
    addModel: () => Promise.reject(new Error('model API not supported by this renderer')),
    removeModel: () => {},
    duplicateModel: () => Promise.reject(new Error('model API not supported by this renderer')),
    setModelTransform: () => {},
    getModelTransform: () => null,
    getModels: () => [] as ViewerModelHandle[],
    pickModel: () => Promise.resolve(null),
    setGizmo: () => {},
    focusModel: () => {},
    setEditMode: () => {},
  };

  constructor(adapter: RendererAdapter) {
    this.adapter = adapter;
  }

  // -------------------------------------------------------------------------
  // 生命周期
  // -------------------------------------------------------------------------

  async init(container: HTMLElement, options: ViewerInitOptions = {}): Promise<void> {
    this.container = container;
    this.initOptions = options;
    await this.adapter.init(container, {
      background: options.background,
      camera: options.camera ?? DEFAULT_CAMERA,
    });
    this.bridgeNavEvents();
  }

  async loadScene(options: ViewerSceneOptions): Promise<void> {
    if (!this.container) {
      throw new Error('AdapterViewer: not initialized');
    }

    // adapter 的 baseRotation 在 init 时注入，场景级旋转需要重建一次 adapter 会话
    if (options.baseRotation) {
      this.unbridgeNavEvents();
      await this.adapter.init(this.container, {
        background: this.initOptions.background,
        camera: this.initOptions.camera ?? DEFAULT_CAMERA,
        baseRotation: options.baseRotation,
      });
      this.bridgeNavEvents();
    }

    this.annotations = options.annotations ?? [];

    const obj = await this.adapter.loadSplat({
      url: options.url,
      settings: options.settings,
      skyboxUrl: options.skyboxUrl,
      collisionUrl: options.collisionUrl,
      collisionBinUrl: options.collisionBinUrl,
      debugCollision: options.debugCollision,
      transform: options.transform,
    });
    this.adapter.addToScene(obj);
    this.emit('ready');
  }

  resize(width: number, height: number): void {
    this.adapter.resize(width, height);
  }

  flushFrame(): void {
    this.adapter.render?.();
  }

  dispose(): void {
    this.unbridgeNavEvents();
    this.listeners.clear();
    this.adapter.dispose();
    this.container = null;
  }

  // -------------------------------------------------------------------------
  // 只读查询
  // -------------------------------------------------------------------------

  getCamera(): CameraOptions {
    if (!this.adapter.getCamera) {
      throw new Error(`Adapter "${this.adapter.id}" does not support getCamera`);
    }
    return this.adapter.getCamera();
  }

  async pick(options: { clientX: number; clientY: number }): Promise<ViewerPickResult | null> {
    const hit = this.adapter.raycast?.(options) ?? null;
    return hit ? { point: hit.point } : null;
  }

  projectPoint(point: Vec3Like): ProjectedPoint | null {
    return this.adapter.projectPoint?.(Vec3.from(point)) ?? null;
  }

  async captureScreenshot(options?: ScreenshotOptions): Promise<Blob> {
    if (!this.adapter.captureScreenshot) {
      throw new Error(`Adapter "${this.adapter.id}" does not support captureScreenshot`);
    }
    return this.adapter.captureScreenshot(options);
  }

  getNavigationMode(): ViewerNavigationMode {
    return this.adapter.getNavigationMode?.() ?? 'orbit';
  }

  isWalkAvailable(): boolean {
    return this.adapter.isWalkAvailable?.() ?? false;
  }

  getAnimState(): ViewerAnimState {
    return IDLE_ANIM_STATE;
  }

  getAnnotations(): ViewerAnnotation[] {
    return this.annotations;
  }

  // -------------------------------------------------------------------------
  // 意图命令
  // -------------------------------------------------------------------------

  setCamera(camera: CameraOptions): void {
    this.adapter.setCamera(camera);
  }

  setNavigationMode(mode: ViewerNavigationMode): void {
    this.adapter.setNavigationMode?.(mode);
  }

  resetNavigation(): void {
    const camera = this.initOptions.camera ?? DEFAULT_CAMERA;
    if (this.adapter.resetNavigation) {
      this.adapter.resetNavigation(camera);
      return;
    }
    this.adapter.setCamera(camera);
  }

  playAnim(): void {
    // 运镜由原生 viewer 闭环实现；shim 不支持
  }

  pauseAnim(): void {}

  togglePlayPause(): void {}

  seekAnim(_time: number): void {}

  setAnimSpeed(_speed: number): void {}

  setAnimTrack(_track: ViewerAnimTrack | null): void {}

  focusAnnotation(index: number): void {
    const annotation = this.annotations[index];
    if (!annotation) return;
    const current = this.adapter.getCamera?.();
    const camera = deriveAnnotationCamera(annotation, current);
    if (this.adapter.flyToCamera) {
      void this.adapter.flyToCamera(camera, { durationMs: 850 });
    } else {
      this.adapter.setCamera(camera);
    }
  }

  // -------------------------------------------------------------------------
  // 三维功能 — AdapterViewer 不支持，调用时报错提示切换原生 viewer
  // -------------------------------------------------------------------------

  isMeasureActive(): boolean {
    return false;
  }

  getAvatarState(): ViewerAvatarState | null {
    return null;
  }

  setInputMode(_mode: ViewerInputMode): void {
    this.notSupported('setInputMode');
  }

  setGamingControls(_enabled: boolean): void {
    this.notSupported('setGamingControls');
  }

  setMeasureActive(_active: boolean): void {
    this.notSupported('setMeasureActive');
  }

  toggleMeasure(): boolean {
    this.notSupported('toggleMeasure');
    return false;
  }

  clearMeasure(): void {
    this.notSupported('clearMeasure');
  }

  setMeasureUnitSystem(_system: 'metric' | 'imperial'): void {
    // AdapterViewer 不支持量测，单位制设置无操作
  }

  setAvatarEnabled(_enabled: boolean): void {
    this.notSupported('setAvatarEnabled');
  }

  setAvatarUrl(_url: string): void {
    this.notSupported('setAvatarUrl');
  }

  setAvatarFollowCamera(_enabled: boolean): void {
    this.notSupported('setAvatarFollowCamera');
  }

  setAvatarMapping(_mapping: Partial<AvatarAnimationMapping>): void {
    this.notSupported('setAvatarMapping');
  }

  enterVr(_preset?: VrQualityPreset): void {}

  setWalkMoveIntent(_intent: WalkMoveIntent): void {
    // walk 意图在 three adapter 中由 NavigationManager 内部处理，此处静默
  }

  setJoystickMoveIntent(_intent: JoystickMoveIntent): void {
    this.notSupported('setJoystickMoveIntent');
  }

  jumpWalk(): void {
    // three adapter 不支持跳跃，静默
  }

  // -------------------------------------------------------------------------
  // 事件
  // -------------------------------------------------------------------------

  on<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb as Listener);
  }

  off<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    this.listeners.get(event)?.delete(cb as Listener);
  }

  // -------------------------------------------------------------------------
  // 内部
  // -------------------------------------------------------------------------

  private notSupported(method: string): void {
    throw new Error(
      `AdapterViewer ("${this.adapter.id}") does not support ${method}. Use a native viewer implementation.`,
    );
  }

  private emit(event: keyof ViewerEventMap, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((cb) => cb(...args));
  }

  private bridgeNavEvents(): void {
    if (!this.adapter.onNavigationModeChange) return;
    this.navModeCallback = (mode) => this.emit('navigationMode:changed', mode);
    this.adapter.onNavigationModeChange(this.navModeCallback);
  }

  private unbridgeNavEvents(): void {
    if (this.navModeCallback) {
      this.adapter.offNavigationModeChange?.(this.navModeCallback);
      this.navModeCallback = null;
    }
  }
}

/** 将 RendererAdapter 包装为 Viewer 契约实现。 */
export function createAdapterViewer(adapter: RendererAdapter): AdapterViewer {
  return new AdapterViewer(adapter);
}
